import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  PublicClientApplication,
  EventType,
  BrowserAuthError,
  type AccountInfo,
  type EventMessage,
  type EventError,
} from '@azure/msal-browser';

/** MSAL fires this during normal redirect-handling; never surface it as a toast. */
const TRANSIENT_CODES = new Set(['interaction_in_progress', 'no_account_error']);

function isTransientMsalError(error: EventError | null | undefined): boolean {
  if (!error) { return false; }
  if (error instanceof BrowserAuthError && TRANSIENT_CODES.has(error.errorCode)) { return true; }
  // Some MSAL builds expose errorCode as a plain property
  const code = (error as { errorCode?: string }).errorCode;
  return typeof code === 'string' && TRANSIENT_CODES.has(code);
}

/**
 * Returns true when the error is a plain JS TypeError/Error thrown inside
 * MSAL (no `errorCode`), as opposed to a real MSAL AuthError.
 * These indicate corrupted sessionStorage redirect state rather than an
 * actual authentication failure the user should see.
 */
function isInternalMsalError(error: EventError | null | undefined): boolean {
  if (!error || typeof error !== 'object') { return false; }
  const code = (error as { errorCode?: unknown }).errorCode;
  return typeof code !== 'string';
}

import { MsalProvider } from '@azure/msal-react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { msalConfig, loginRequest } from '@shared/auth/msal.config';
import { gatewaysApiRequest } from '@shared/auth/msal.config';
import { environment } from '@environments/environment';
import { ThemeProvider } from '@shared/theme/theme.provider';
import { PreferencesHydrator } from '@shared/theme/preferences-hydrator';
import { createHttpClient } from '@shared/http/http-client';
import { RepositoriesProvider } from '@data/data.providers';
import { GatewaysHttpClientProvider } from '@data/data.providers';
import { ServicesProvider } from '@domain/services.providers';
import { emitToast } from '@components/toast';
import { initI18n, i18n } from '@shared/i18n/i18n.config';
import { initAppInsights, trackException } from '@shared/observability/app-insights';
import { TelemetryRouterListener } from '@shared/observability/telemetry-router-listener';
import { createDevMsalStub } from '@shared/auth/dev-msal-stub';
import { App } from './app/App';
import '@styles/typography';
import '@styles/global.scss';

initI18n();
initAppInsights();

if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (ev) => {
    trackException(ev.reason instanceof Error ? ev.reason : new Error(String(ev.reason)), {
      source: 'unhandledrejection',
    });
  });
}

const AUTH_BYPASS = import.meta.env.DEV && import.meta.env.VITE_AUTH_BYPASS === 'true';
/**
 * UI-15 — visual regression hook. When the Playwright suite spins
 * up `vite dev` it sets `VITE_VISUAL_MOCKS=true`, which makes us
 * register an MSW worker that returns deterministic mockup data
 * for every `/api/...` route. Tree-shaken away in production via
 * the `import.meta.env.DEV` guard.
 */
const VISUAL_MOCKS = import.meta.env.DEV && import.meta.env.VITE_VISUAL_MOCKS === 'true';

const msalInstance = AUTH_BYPASS
  ? createDevMsalStub()
  : new PublicClientApplication(msalConfig);

async function startVisualMocks(): Promise<void> {
  if (!VISUAL_MOCKS) { return; }
  // Pin "now" to the same instant the MSW fixtures anchor on so
  // every relative-time render (greetings, due-in-24h, lunch
  // schedule) is deterministic across runs.
  const FROZEN_NOW = Date.parse('2026-04-24T08:30:00.000Z');
  const RealDate = Date;
  class FrozenDate extends RealDate {
    constructor(...args: unknown[]) {
      if (args.length === 0) {
        super(FROZEN_NOW);
        return;
      }
      // Forwarding heterogeneous Date ctor args (number | string | Date | y,m,d,...).
      super(...(args as ConstructorParameters<typeof RealDate>));
    }
    static override now(): number { return FROZEN_NOW; }
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).Date = FrozenDate;

  const { visualWorker } = await import('./test/mocks/visual-browser');
  await visualWorker.start({
    onUnhandledRequest: 'bypass',
    serviceWorker: { url: '/mockServiceWorker.js' },
  });
}

/**
 * Hard guard against the redirect-storm loop.
 *
 * Symptom (the bug we just fixed): the app rapidly fired `loginRedirect`
 * dozens of times per second, Entra answered with success each time, and
 * Microsoft eventually showed "We couldn't sign you in. Please try again."
 *
 * Root cause: the MSAL response (`#code=...`) was processed AFTER React
 * mounted, so `RequireAuth`'s effect saw `inProgress === None &&
 * isAuthenticated === false` for one render and fired another redirect
 * before MsalProvider's internal `handleRedirectPromise()` finished
 * consuming the fragment.
 *
 * This guard prevents *any* code path (RequireAuth, KMSI handler, axios
 * interceptor) from firing more than one interactive redirect per second.
 */
const REDIRECT_LOCK_KEY = 'dcl.msal.last-redirect';
const REDIRECT_LOCK_MS = 2_000;
function isRedirectLocked(): boolean {
  try {
    const last = Number(sessionStorage.getItem(REDIRECT_LOCK_KEY) ?? '0');
    return Number.isFinite(last) && Date.now() - last < REDIRECT_LOCK_MS;
  } catch { return false; }
}
function markRedirect(): void {
  try { sessionStorage.setItem(REDIRECT_LOCK_KEY, String(Date.now())); } catch { /* noop */ }
}
// Expose for other modules (RequireAuth, auth.interceptor) without coupling on a shared module.
(window as unknown as { __dclRedirectGuard?: { isLocked: () => boolean; mark: () => void } })
  .__dclRedirectGuard = { isLocked: isRedirectLocked, mark: markRedirect };

// Capture the KMSI error flag BEFORE handleRedirectPromise() clears the URL.
const redirectFragment = typeof window !== 'undefined' ? window.location.hash.slice(1) : '';
const redirectSearch = typeof window !== 'undefined' ? window.location.search.slice(1) : '';
const hasKmsiRedirectError =
  redirectFragment.includes('50140') || redirectSearch.includes('50140');
// `#code=...` / `#error=...` indicates an active MS redirect that MUST be consumed before React mounts.
const hasAuthCode = redirectFragment.includes('code=') || redirectSearch.includes('code=');
const hasAuthResponse = hasAuthCode || redirectFragment.includes('error=') || redirectSearch.includes('error=');

/**
 * Clears the MSAL-internal `interaction_in_progress` lock from sessionStorage.
 *
 * MSAL writes `interaction.status = interaction_in_progress` before navigating
 * away to Entra and clears it in `handleRedirectPromise()` on return.  If the
 * user closed the tab, refreshed at the wrong moment, or an extension disrupted
 * the redirect, the lock can be left behind.  On the next visit MSAL sees it and
 * refuses to start a new interaction, leaving the app on a blank page.
 *
 * We only clear it when there is NO active auth response in the URL — i.e. this
 * is a fresh page load, not a return from Entra — so we never interfere with a
 * real in-flight auth exchange.
 */
function clearStaleInteractionLock(): void {
  if (hasAuthResponse) { return; } // active redirect — do not touch MSAL state
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.includes('interaction.status')) {
        const value = sessionStorage.getItem(key) ?? '';
        // MSAL v3 InteractionStatus values: 'login', 'logout', 'acquireToken',
        // 'ssoSilent', 'handleRedirect', 'startup'. Only 'none' is quiescent.
        // Note: the old check for 'interaction_in_progress' was wrong — that is
        // an error code, not an InteractionStatus value; this bug meant the lock
        // was never cleared.
        if (value.length > 0 && value !== 'none') {
          keysToRemove.push(key);
        }
      }
    }
    keysToRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch { /* sessionStorage may be blocked in some environments */ }
}

if (!AUTH_BYPASS) {
  clearStaleInteractionLock();
}

void startVisualMocks().then(() => msalInstance.initialize()).then(async () => {
  // CRITICAL: process the redirect fragment BEFORE React mounts. The official MSAL
  // recommendation is to await `handleRedirectPromise()` in app bootstrap so that
  // (a) the auth code is exchanged for tokens, (b) the URL hash is cleared, and
  // (c) `navigateToLoginRequestUrl: true` runs its `window.location.replace` to
  // the original page — which causes a full reload of main.tsx with a clean URL
  // and a hydrated MSAL cache. Doing this AFTER React mounts (inside MsalProvider)
  // creates a race where RequireAuth fires a fresh loginRedirect during the gap.
  if (!AUTH_BYPASS) {
    try {
      const result = await msalInstance.handleRedirectPromise();
      if (result?.account) {
        msalInstance.setActiveAccount(result.account);
      }
      // If MSAL is navigating away (navigateToLoginRequestUrl: true), the line above
      // already triggered window.location.replace — execution may continue briefly
      // but the page is unloading. Returning here is harmless.
    } catch (err) {
      // A redirect-processing failure is most commonly caused by a browser extension
      // blocking the POST to login.microsoftonline.com/token (shows as
      // '(blocked:other)' in DevTools). Incognito works because extensions are
      // disabled there by default.
      //
      // CRITICAL: clear the auth-code fragment from the URL so RequireAuth does not
      // think a redirect is still in progress and stays permanently silent.
      // The auth code is single-use; it cannot be retried — a fresh loginRedirect
      // is needed after the user fixes the extension.
      if (
        window.location.search.includes('code=') ||
        window.location.hash.includes('code=')
      ) {
        window.history.replaceState({}, '', window.location.pathname);
      }
      // Store a flag so RequireAuth can show an actionable error message.
      try { sessionStorage.setItem('dcl.msal.exchange-failed', '1'); } catch { /* noop */ }
      trackException(err instanceof Error ? err : new Error(String(err)), {
        source: 'msal.handleRedirectPromise',
      });
    }
  }

  const accounts = msalInstance.getAllAccounts();
  if (!msalInstance.getActiveAccount() && accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  // AADSTS50140 (KMSI "Stay signed in?") interrupted the previous auth attempt.
  // After handleRedirectPromise() above, the URL hash is already cleared, so any
  // subsequent loginRedirect captures a clean loginStartPage.
  if (hasKmsiRedirectError && !msalInstance.getActiveAccount() && !AUTH_BYPASS) {
    if (isRedirectLocked()) {
      // Another redirect just fired — abort to avoid a storm. Mount React so the
      // user at least sees an error rather than a flickering blank page.
    } else {
      try {
        const silentResult = await msalInstance.ssoSilent({ scopes: [...loginRequest.scopes] });
        if (silentResult?.account) { msalInstance.setActiveAccount(silentResult.account); }
      } catch {
        markRedirect();
        msalInstance
          .loginRedirect({ scopes: [...loginRequest.scopes], prompt: 'select_account' })
          .catch(() => { /* navigation already in progress */ });
        return; // Do NOT mount React while redirect is navigating away.
      }
    }
  }
  // Suppress unused-var TS warning for `hasAuthCode` (kept for future diagnostics).
  void hasAuthCode;

  msalInstance.addEventCallback((event: EventMessage) => {
    switch (event.eventType) {
      case EventType.LOGIN_SUCCESS: {
        if (event.payload && 'account' in event.payload) {
          msalInstance.setActiveAccount(
            (event.payload as { account: AccountInfo }).account,
          );
        }
        return;
      }
      case EventType.LOGIN_FAILURE: {
        if (isTransientMsalError(event.error)) { return; }
        if (isInternalMsalError(event.error)) {
          // Internal (non-AuthError) MSAL failure. Previously we cleared sessionStorage
          // and reloaded — that destroys the PKCE verifier mid-flight and creates a
          // different loop. Just log and let RequireAuth's redirect-lock decide what
          // to do next.
          trackException(toError(event.error), { source: 'msal.LOGIN_FAILURE.internal' });
          return;
        }
        // AADSTS50140 (KMSI interrupt) is handled pre-React in the initialize()
        // .then() above (hasKmsiRedirectError check) to avoid race conditions.
        // Log it here as a safety net for any non-redirect occurrence.
        const errMsg = (event.error as { message?: string } | null)?.message ?? '';
        if (errMsg.includes('50140')) {
          trackException(toError(event.error), { source: 'msal.LOGIN_FAILURE.kmsi' });
          return; // RequireAuth will trigger a fresh loginRedirect on next render.
        }
        emitToast({
          level: 'error',
          message: formatAuthError(i18n.t('msal.signInFailed', 'Sign-in failed'), event.error),
        });
        trackException(toError(event.error), { source: 'msal.LOGIN_FAILURE' });
        return;
      }
      case EventType.ACQUIRE_TOKEN_FAILURE: {
        if (isTransientMsalError(event.error)) { return; }
        if (isInternalMsalError(event.error)) {
          trackException(toError(event.error), { source: 'msal.ACQUIRE_TOKEN_FAILURE.internal' });
          return;
        }
        emitToast({
          level: 'warning',
          message: formatAuthError(i18n.t('msal.tokenRefreshFailed', 'Token refresh failed'), event.error),
        });
        trackException(toError(event.error), { source: 'msal.ACQUIRE_TOKEN_FAILURE' });
        return;
      }
      case EventType.LOGOUT_SUCCESS: {
        emitToast({ level: 'info', message: i18n.t('msal.signedOut', 'You have been signed out.') });
        return;
      }
      default:
        return;
    }
  });

  const http = createHttpClient(msalInstance);
  const gatewaysHttp = createHttpClient(msalInstance, {
    baseURL: environment.gatewaysApi.baseUrl,
    scopes: gatewaysApiRequest.scopes,
  });

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { retry: 1, refetchOnWindowFocus: false },
    },
  });

  // Handle bfcache (Back-Forward Cache) restoration in normal browsers.
  // When a user navigates away to Entra for login and the browser restores
  // the previous page from bfcache on return (instead of running main.tsx
  // again), the msalInstance in memory is stale and tokens may not be set.
  // The `pageshow` event fires on bfcache restores with `event.persisted = true`.
  // Re-running handleRedirectPromise() is safe (returns null when there's
  // nothing to handle) and ensures the active account is set correctly.
  if (!AUTH_BYPASS) {
    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
      if (!event.persisted) { return; }
      void msalInstance.handleRedirectPromise().then((result) => {
        if (result?.account) {
          msalInstance.setActiveAccount(result.account);
        }
        if (!msalInstance.getActiveAccount()) {
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) { msalInstance.setActiveAccount(accounts[0]); }
        }
      }).catch(() => { /* bfcache restore — ignore stale redirect errors */ });
    });
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <RepositoriesProvider http={http} gatewaysHttp={gatewaysHttp}>
            <GatewaysHttpClientProvider client={gatewaysHttp}>
            <ServicesProvider>
              <ThemeProvider>
                <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                  <PreferencesHydrator>
                    <TelemetryRouterListener />
                    <App />
                  </PreferencesHydrator>
                </BrowserRouter>
              </ThemeProvider>
            </ServicesProvider>
            </GatewaysHttpClientProvider>
          </RepositoriesProvider>
        </QueryClientProvider>
      </MsalProvider>
    </React.StrictMode>,
  );
});

function formatAuthError(prefix: string, error: EventError | null | undefined): string {
  if (!error) { return prefix; }
  if (typeof error === 'string') { return `${prefix}: ${error}`; }
  try {
    // `AuthError` (subtype of Error) carries a richer message via
    // `errorMessage`; fall back to the plain `message` / `errorCode`.
    const errLike = error as { errorMessage?: unknown; message?: unknown; errorCode?: unknown };
    const detail =
      (typeof errLike.errorMessage === 'string' && errLike.errorMessage) ||
      (typeof errLike.message === 'string' && errLike.message) ||
      (typeof errLike.errorCode === 'string' && errLike.errorCode);
    return detail ? `${prefix}: ${detail}` : prefix;
  } catch {
    return prefix;
  }
}

function toError(error: EventError | null | undefined): Error {
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === 'string') {
    return new Error(error);
  }
  return new Error('Unknown MSAL error');
}
